<template>
  <div id="app" class="v2_bg_white" :class="{ 'side-bar--expanded': showAdminMenu }">
    <navigation-layout v-if="isHeaderVisible" />
    <div
      :class="[
        'main-content',
        'position-relative',
        { 'pb-0': !isFooterVisible },
        { 'pt-0': !isHeaderVisible },
      ]"
    >
      <main-layout />
      <router-view :key="$route.fullPath" style="margin-top: 80px"/>
      <footer-layout v-if="isFooterVisible" />
    </div>
    <HelpAlertModal />
    <HelpConfirmModal />
    <portal-target name="info-popups" />
    <portal-target name="additional-popups" />
  </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import 'vue-material-design-icons/styles.css';
import FooterLayout from '@/components/layouts/FooterLayout';
import MainLayout from '@/components/layouts/MainLayout';
import NavigationLayout from '@/components/layouts/NavigationLayout';
import HelpAlertModal from '@/components/HelpAlertModal';
import HelpConfirmModal from '@/components/HelpConfirmModal';

export default {
  name: 'app',
  components: {
    FooterLayout,
    MainLayout,
    NavigationLayout,
    HelpAlertModal,
    HelpConfirmModal,
  },
  computed: {
    isUser: function () {
      return this.$store.state.user;
    },
    showAdminMenu: function () {
      return this.$route.path.indexOf('/admin') === 0;
    },
    isHeaderVisible: function () {
      return ['/presentation/reports'].every(
        (path) => !this.$route.path.startsWith(path)
      );
    },
    isFooterVisible: function () {
      return ['/presentation/reports', '/surveyForm'].every(
        (path) => !this.$route.path.startsWith(path)
      );
    },
  },
};
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
@import './../node_modules/bootstrap/scss/bootstrap';
@import './../node_modules/@mdi/font/css/materialdesignicons.css';
@import './../node_modules/bootstrap-vue/dist/bootstrap-vue.css';
@import './../node_modules/nouislider/distribute/nouislider.css';
// include this in any Vue component where you want to reference vars or mixins
@import '../sass/variables_mixins.scss';
@import '../sass/v2_mixins';
@import '../sass/v3';

// UTILITY CLASSES

html {
  /*font-size: 62.5%; !* 62.5% of 16px = 10px *!*/
  /*font-size: 87.5%; !* 87.5% of 16px = 14px *!*/
}

.offscreen {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
}

.small-heading {
  text-transform: uppercase;
  font-weight: bold;
}

.icon.white {
  use {
    fill: #fff !important;
  }
}

.menu-dropdown {
  padding: 10px 0;
  button {
    background-color: transparent !important;
    padding: 0;
  }

  li.nav-item {
    a {
      color: #000 !important;
    }
    &:hover {
      background-color: rgba($phairify_primary_color_teal, 0.25);
    }
  }

  .nav-link {
    color: #000 !important;
  }

  .no-wrap {
    white-space: nowrap;
  }

  /*ul {*/
  /*    margin-top: 15px;*/
  /*    background-color: #12478F !important;*/
  /*    color: #fff;*/

  /*    li {*/
  /*        padding: 5px 10px;*/
  /*    }*/
  /*}*/
}

/*
    svg {
      vertical-align: initial;
    }

    svg.icon {
      width:16px;
      height:16px;
    }

    .btn {
      svg.icon {
        width:12px;
        height:12px;
      }
      use {fill:#fff;}
    }
  */

img.icon {
  height: 16px;
  width: 16px;
}

.dragArea {
  min-height: 30px;
  padding: 10px;
  display: block;
  background: #fff !important;

  .card-header {
    h5 {
      width: 80%;
    }
  }
}

.deleteMe {
  opacity: 0.3;
}

.half-circle-spinner {
  margin: 30px auto;
  animation-duration: 2000ms !important;
  height: 70px !important;
  width: 70px !important;

  div {
    border-top-color: $phairify_primary_color_teal !important;
    border-bottom-color: $phairify_primary_color_teal !important;
  }
}

.spinner-message {
  color: $phairify_primary_color_teal;
}

.required-badge {
  font-weight: bold;
}

.debug {
  // TODO: auto-hide in production?
  color: #ccc;
}

// HTML ELEMENTS

img {
  max-width: 100%;
}

a,
.btn-link {
  color: $v3_color_teal;

  &:hover {
    color: $v3_color_blue_lagoon;
  }
}
.page-link {
  color: $phairify_secondary_color_grey;

  &:hover {
    color: $phairify_primary_color_teal;
    background-color: rgba($phairify_secondary_color_sky, 0.5);
    outline: rgba($phairify_secondary_color_sky, 0.5);
  }

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba($phairify_secondary_color_sky, 0.25);
  }
}

.mb--5px {
  margin-bottom: -5px;
}

// TYPOGRAPHY

h1,
h2,
h3,
h4 {
  font-family: $phairify_font_montserrat;
  margin-bottom: 0 !important;
}

@media (max-width: 767px) {
  h1 {
    font-size: 1.75rem;
    line-height: 1;
  }

  h2 {
    font-size: 1.5rem;
  }

  h3 {
    font-size: 1.35rem;
  }

  h4 {
    font-size: 1.15rem;
  }
}

#app {
  font-family: $phairify_font_montserrat;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: left;
  color: $phairify_dark_sky_blue;
}

// LAYOUT

.main-content {
  min-height: $phairify_main_content_height;
  padding-top: $phairify_navigation_height;
  padding-bottom: $phairify_footer_height;
  > .container {
    padding-left: 30px;
    padding-right: 30px;
  }
  @media (max-width: 576px) {
    padding-bottom: $phairify_mobile_footer_height;
  }
}

// HEADER
.app-header {
  min-height: $phairify_navigation_height !important;
  background-color: $phairify_primary_color_teal !important;
}

.app_name {
  @include phairify_heading_semibold;
  font-weight: 400;
  display: inline-block;
  text-transform: uppercase;
  margin-left: 20px;
  margin-bottom: 0;
}

.logo {
  max-width: $phairify_icon-size_logo;
}

nav {
  margin-bottom: 20px;
}

.nav-link {
  padding: 0;
}

// MAIN MENU

nav {
  margin-bottom: 0;
}

.navbar-dark .navbar-nav {
  /*.identity__dropdown {
            > a, > :hover, a:focus {
                color: lighten($link-color, 30%);
            }
        }*/
  .nav-item {
    display: flex;
    align-items: center;
    margin-left: 20px;

    @media (max-width: 1200px) {
      margin-left: 12px;
    }

    .nav-link {
      color: #fff;
      display: flex;
      align-items: center;
      outline: none;

      &.router-link-exact-active {
        color: $phairify_secondary_color_lime;
        .material-design-icon {
          .material-design-icon__svg {
            fill: $phairify_secondary_color_lime;
          }
        }
        .faq-icon::before {
          content: '';
          display: inline-block;
          height: 18px;
          width: 18px;
          background: white;
          border-radius: 50%;
          position: absolute;
          top: 8px;
          right: 5px;
        }
      }

      &-title {
        @include phairify_heading_bold_16;
        font-weight: 400;
        margin-bottom: 0;
      }

      &.active {
        color: lighten($link-color, 30%);
      }

      .dropdown-toggle {
        display: flex;
        align-items: center;
      }
    }
  }
}

/*.identity__dropdown .dropdown-menu {
        @media (max-width: 991px) {
            margin-left: 15px;
            margin-right: 15px;
        }
    }*/

.navbar {
  padding-top: 0;
  padding-bottom: 0;
  border: 2px solid #2f528f;
  z-index: 20;
  @media (max-width: 991px) {
    padding-left: 0;
    padding-right: 0;
  }

  @media (min-width: 992px) {
    width: 100%;
    height: 56px;
    min-height: 56px;
  }

  .navbar-brand {
    display: flex;
    align-items: center;
    outline: none;
  }
}

@media (min-width: 992px) {
  .navbar-expand-lg .navbar-nav {
    a.nav-link {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }

    li.nav-link {
      padding-left: 0;
      padding-right: 0;
    }
  }
}

.navbar-collapse {
  z-index: 1;
  @media (max-width: 991px) {
    text-align: left;
    padding-top: 1rem;
    .nav-item > .nav-link {
      padding: 0.25rem 1rem;
      font-size: 18px;
      letter-spacing: 2px;
    }
  }
  @media (min-width: 992px) {
    justify-content: flex-end;
  }
}

.navbar-toggler {
  margin-left: 15px;
}

.navbar-toggler:hover,
.navbar-toggler:focus {
  outline: none;
}

#side-bar {
  height: calc(100% - 56px);
}

// BUTTONS

.btn:focus/*:not(.btn-link)*/,
.btn.focus/*:not(.btn-link)*/ {
  //@include box-shadow(0 0 0 .2rem rgba(153,197,85,.3));
  @include focus-green;
}

.btn-outline-success:hover {
  background-color: lighten($green, 25%);
}

.btn-info {
  background-color: $green !important;
  color: #333;

  &:hover {
    color: #333;
    background-color: lighten($green, 10%) !important;
  }

  &.disabled,
  &:disabled {
    color: #333;
  }
}

.btn-wide {
  width: 100%;
}

.btn-large {
  padding: 0.75em 2em;
  font-size: 1.25em;
}

.btn svg.icon {
  fill: #333 !important;
}

.btn-group > .btn:not(:last-child):not(.dropdown-toggle),
.btn-group > .btn-group:not(:last-child) > .btn {
  border-right: solid 2px lighten($green, 10%);
}

.btn-ghost {
  background-color: transparent;
  border-radius: 4px;
  border-color: $link-color;
  padding: 0;

  &:hover {
    border-color: lighten($link-color, 20%);
  }

  a,
  span {
    display: inline-block;
    padding: 0.2rem 0.6rem;

    &:hover {
      text-decoration: none;
    }
  }
}

.icon-input {
  position: relative;
}

.icon-input__icon {
  position: absolute;
  @include vertical-center;
  left: 0.75rem;
  z-index: 1;
}

.icon-input__icon-right {
  position: absolute;
  @include vertical-center;
  right: 0.75rem;
}

.icon-input__input {
  padding-left: 3rem;
}

.form-control:focus {
  @include focus-blue;
  border-color: $accent-color;
}

.bg-info {
  //   background-color: $green !important;
}

.alert-info {
  background-color: lighten($green, 25%) !important;
  border-color: $green;
}

.badge-info {
  @include bg-override;
}

a[role='tab'].active {
  background-color: $button-color !important;
  color: #333 !important;
}

svg.icon {
  fill: $base-color !important;
}

.table {
  background-color: #fff;
}

.table th,
.table td {
  text-align: left;
}

.page-item.active .page-link {
  background-color: $phairify_secondary_color_sky;
  color: $phairify_primary_color_teal;
  border-color: $phairify_secondary_color_sky;
}

td .question-edit .btn:first-child {
  margin-bottom: 3px;
}
.custom-checkbox
  .custom-control-input:disabled:checked
  ~ .custom-control-label::before {
  background-color: $phairify_primary_color_teal;
}

.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: $phairify_primary_color_teal;
}

.custom-control ~ .custom-control-label::before {
  background-color: $phairify_primary_color_teal;
}

.custom-control-input:checked ~ .custom-control-label::before {
  background-color: $phairify_primary_color_teal;
}

.custom-radio .custom-control-input:checked ~ .custom-control-label::before {
  background-color: $phairify_primary_color_teal;
  border-color: $phairify_primary_color_teal;
}

.custom-checkbox
  .custom-control-input:indeterminate
  ~ .custom-control-label::before {
  background-color: rgba($phairify_primary_color_teal, 0.7);
  border-color: rgba($phairify_primary_color_teal, 0.7);
}

.custom-control-input:focus ~ .custom-control-label::before {
  @include focus-blue;
}

.custom-checkbox,
.custom-radio {
  .custom-control-label {
    margin-left: 3px;
  }
}

.section-title.current {
  color: $link-color !important;
}

@media (min-width: $tablet-portrait) and (min-height: 700px) {
  .survey-form {
    position: relative;
    height: 100%;
    padding-bottom: 20px;
  }

  .survey-form__body {
    max-height: 90%;
  }

  .survey-form__footer {
    // position: absolute;
    // bottom: 0;
    //width: 100%;
    //height: 90px;
  }
}

html,
body {
  height: 100%;
}

.footer {
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  height: $phairify_footer_height;
  margin-top: auto;
  text-align: center;
  padding-top: 2rem;
  padding-bottom: 2rem;
  //flex-shrink: 0;
  @include font-questrial;
  @media (max-width: 576px) {
    height: $phairify_mobile_footer_height;
  }
}

@media (min-width: $tablet-portrait) {
  .main-content {
    display: flex;
    flex-direction: column;
  }
}

.footer__social {
  font-size: 13px;
  color: #000;
  &--link {
    vertical-align: 0.5em;
  }
  &--icon {
    width: 25px;
  }
}

.footer__copyright {
  font-size: 13px;
  color: $phairify_footer_grey_color;
}

.footer__branding {
}

.card {
  @include pharify_border;
}

.card-title {
  margin-left: -20px;
  margin-right: -20px;
  padding-left: 20px;
  padding-bottom: 15px;
  border-bottom: solid 1px #ddd;
}

.card-header {
  background-color: initial;
  padding: 5px 10px;
  border-bottom: none;
}

.card-body {
  padding: 5px 10px;
}
.card-footer {
  background-color: #ffffff;
  border-top: 1px solid rgba($phairify_secondary_color_grey, 0.25);
}

.card__view-more {
  //float:right;
  //margin-top:auto;
  position: absolute;
  bottom: 1em;
  right: 20px;

  svg {
    @include transition(all 0.3s);
  }

  use {
    fill: $link-color;
  }

  &:hover {
    text-decoration: none;

    svg {
      @include transform(translateX(5px));
    }

    use {
      fill: lighten($link-color, 15%);
    }
  }
}

.material-design-icon {
  height: $phairify_icon-size_large;
  width: $phairify_icon-size_large;

  &.md-icon-32 {
    .material-design-icon__svg {
      height: $phairify_icon-size_logo;
      width: $phairify_icon-size_logo;
    }
  }
}

.material-design-icon > .material-design-icon__svg {
  height: $phairify_icon-size_large;
  width: $phairify_icon-size_large;
  fill: #ffffff;
}

.material-design-icon.help-circle-icon {
  bottom: 0.15em;
}

.material-design-icon.cog-icon {
  bottom: 0.1em;
}

.material-design-icon.account-icon {
  bottom: -0.1em;
}

.membership-title {
  @include phairify_heading_not_bold_16;
  margin-bottom: 0;
  padding: 0 10px;
}

.phairify-border-right-sky {
  border-right: 2px solid rgba($phairify_secondary_color_sky, 0.25) !important;
  border-top: 0;
  border-bottom: 0;
  border-left: 0;
}
.phairify-border-bottom-sky {
  border-bottom: 2px solid rgba($phairify_secondary_color_sky, 0.25) !important;
  border-top: 0;
  border-right: 0;
  border-left: 0;
}
.phairify-border-top-sky {
  border-top: 2px solid rgba($phairify_secondary_color_sky, 0.25) !important;
  border-right: 0;
  border-left: 0;
  border-bottom: 0;
}
.phairify-border-left-sky {
  border-left: 2px solid rgba($phairify_secondary_color_sky, 0.25) !important;
  border-right: 0;
  border-top: 0;
  border-bottom: 0;
}

.modal-header,
.modal-body {
  padding: 10px 20px;
}
.custom-checkbox .custom-control-label::before {
  border-radius: 0;
}
.custom-checkbox .custom-control-label::after,
.custom-checkbox .custom-control-label::before {
  top: 0.2rem;
}
.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: $phairify_primary_color_teal;
  border-color: $phairify_primary_color_teal;
}
.custom-select {
  &:focus {
    border-color: $phairify_primary_border_color;
    box-shadow: 0 0 0 0.2rem rgba($phairify_primary_color_icon, 0.25) !important;
    outline-color: $phairify_primary_border_color;
  }
}

.mdi {
  font-size: $phairify_icon-size_large;
  &.mdi-information-outline {
    @extend .font_size_1325rem;
  }
}
.phairify-icon-size-36 {
  font-size: $phairify_icon-size_xl !important;
}

.phairify-color-teal {
  color: $phairify_primary_color_teal !important;
}

.phairify-color-darken_red {
  color: $phairify_color_darken_red !important;
}

.phairify-color-grey {
  color: $phairify_secondary_color_grey !important;
}

.phairify-color-navy {
  color: $phairify_primary_color_navy !important;
}

.phairify-color-lime {
  color: $phairify_secondary_color_lime !important;
}

.phairify-color-red {
  color: $phairify_color_red !important;
}

.btn.btn-primary {
  @include phairify_primary_button_enabled;
  &.disabled {
    @include phairify_primary_button_disabled;
  }
}

.btn.btn-white {
  @include phairify_white_button;
}
.btn.btn-secondary {
  @include phairify_secondary_button;
}
.btn.btn-menu {
  color: #ffffff;
  background-color: transparent;
  border: none;
  outline: none;
}
.btn.btn-dots {
  color: $phairify_secondary_color_grey;
  &:not(.p-0) {
    padding: 4px 0 !important;
  }
  &:disabled {
    color: rgba($phairify_secondary_color_grey, 0.5);
  }
}
.phairify-border-grey {
  border: 1px solid rgba($phairify_secondary_color_grey, 0.25);
}

.phairify-border-rounded {
  border-radius: $phairify_border_radius !important;
}

.phairify-border-right-grey {
  border-right: 2px solid rgba($phairify_secondary_color_grey, 0.25) !important;
  border-top: 0;
  border-bottom: 0;
  border-left: 0;
}
.phairify-border-top-grey {
  border-top: 2px solid rgba($phairify_secondary_color_grey, 0.25) !important;
  border-right: 0;
  border-left: 0;
  border-bottom: 0;
}
.phairify-border-left-grey {
  border-left: 2px solid rgba($phairify_secondary_color_grey, 0.25) !important;
  border-right: 0;
  border-top: 0;
  border-bottom: 0;
}

.vertical-align-middle {
  height: 100%;
  display: inline-block;
  vertical-align: middle;
}

.body-copy-font {
  @include phairify_body_copy;
}

.other-content-font {
  @include phairify_body_copy;
}

.list-content-font {
  @include phairify_list_content;
}

.admin-table {
  @include pharify_admin_table;
}

.phairify-heading-h2 {
  @include phairify_heading_not_bold_18;
}

.phairify-heading-not-bold {
  @include phairify_heading_not_bold_20;
}

.phairify-select-wrapper {
  position: relative;

  .custom-select {
    position: relative;
    background: transparent;
    border-radius: 0;
    color: $phairify_v2_color_black;
    z-index: 10;

    &:disabled {
      color: rgba($v3_color_teal, 0.5);
    }
  }

  &::after {
    content: '\F035D';
    display: inline-block;
    font: normal normal normal 24px/1 'Material Design Icons';
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    font-size: $phairify_icon-size_logo;
    color: $phairify_primary_color_navy;
    z-index: 1;
  }
  &.disabled::after {
    color: rgba($phairify_secondary_color_grey, 0.5);
  }
}
.phairify-h2-bold {
  @include phairify_heading_bold_18;
  line-height: 1.5;
}

.phairify-h2 {
  @include phairify_heading_not_bold_18;
  line-height: 1.5;
}

.clickable:hover {
  cursor: pointer;
}

.toasted-container {
  &.top-right {
    top: 16% !important;
    right: 7%;
  }
  .toasted {
    &.primary {
      &.success {
        background-color: $phairify_secondary_color_lime;
      }
    }
  }
}

.toasted-container .toasted .action.icon.mdi.toaster-close-icon {
  font-size: 18px;
  color: inherit;
}

.v2_info-popup__title {
  @include v2_info_popup_title;
}
.v2_info-popup__body {
  @extend .font_Montserrat;
  @extend .font_size_1000rem;
  @extend .v3_color_black_knight;
}

.v2_info-popup__footer {
  @include v2_info_popup_body;
}

.p-6 {
  padding: 4.5rem !important;
}
.pt-6,
.py-6 {
  padding-top: 4.5rem !important;
}
.pr-6,
.px-6 {
  padding-right: 4.5rem !important;
}
.pb-6,
.py-6 {
  padding-bottom: 4.5rem !important;
}
.pl-6,
.px-6 {
  padding-left: 4.5rem !important;
}
@media (min-width: 576px) {
  .p-sm-6 {
    padding: 4.5rem !important;
  }
  .pt-sm-6,
  .py-sm-6 {
    padding-top: 4.5rem !important;
  }
  .pr-sm-6,
  .px-sm-6 {
    padding-right: 4.5rem !important;
  }
  .pb-sm-6,
  .py-sm-6 {
    padding-bottom: 4.5rem !important;
  }
  .pl-sm-6,
  .px-sm-6 {
    padding-left: 4.5rem !important;
  }
  .border {
    &-sm {
      &-0 {
        border: 0 solid !important;
      }
      &-1 {
        border: 1px solid !important;
      }
      &-2 {
        border: 2px solid !important;
      }
    }
    &-top {
      &-sm {
        &-0 {
          border-top: 0 solid !important;
        }
        &-1 {
          border-top: 1px solid !important;
        }
        &-2 {
          border-top: 2px solid !important;
        }
      }
    }
    &-right {
      &-sm {
        &-0 {
          border-right: 0 solid !important;
        }
        &-1 {
          border-right: 1px solid !important;
        }
        &-2 {
          border-right: 2px solid !important;
        }
      }
    }
    &-left {
      &-sm {
        &-0 {
          border-left: 0 solid !important;
        }
        &-1 {
          border-left: 1px solid !important;
        }
        &-2 {
          border-left: 2px solid !important;
        }
      }
    }
    &-bottom {
      &-sm {
        &-0 {
          border-bottom: 0 solid !important;
        }
        &-1 {
          border-bottom: 1px solid !important;
        }
        &-2 {
          border-bottom: 2px solid !important;
        }
      }
    }
  }

  .position {
    &-sm {
      &-static {
        position: static !important;
      }
      &-relative {
        position: relative !important;
      }
      &-absolute {
        position: absolute !important;
      }
      &-fixed {
        position: fixed !important;
      }
      &-sticky {
        position: -webkit-sticky !important;
        position: sticky !important;
      }
    }
  }
}
@media (min-width: 768px) {
  .p-md-6 {
    padding: 4.5rem !important;
  }
  .pt-md-6,
  .py-md-6 {
    padding-top: 4.5rem !important;
  }
  .pr-md-6,
  .px-md-6 {
    padding-right: 4.5rem !important;
  }
  .pb-md-6,
  .py-md-6 {
    padding-bottom: 4.5rem !important;
  }
  .pl-md-6,
  .px-md-6 {
    padding-left: 4.5rem !important;
  }

  .border {
    &-md {
      &-0 {
        border: 0 solid !important;
      }
      &-1 {
        border: 1px solid !important;
      }
      &-2 {
        border: 2px solid !important;
      }
    }
    &-top {
      &-md {
        &-0 {
          border-top: 0 solid !important;
        }
        &-1 {
          border-top: 1px solid !important;
        }
        &-2 {
          border-top: 2px solid !important;
        }
      }
    }
    &-right {
      &-md {
        &-0 {
          border-right: 0 solid !important;
        }
        &-1 {
          border-right: 1px solid !important;
        }
        &-2 {
          border-right: 2px solid !important;
        }
      }
    }
    &-left {
      &-md {
        &-0 {
          border-left: 0 solid !important;
        }
        &-1 {
          border-left: 1px solid !important;
        }
        &-2 {
          border-left: 2px solid !important;
        }
      }
    }
    &-bottom {
      &-md {
        &-0 {
          border-bottom: 0 solid !important;
        }
        &-1 {
          border-bottom: 1px solid !important;
        }
        &-2 {
          border-bottom: 2px solid !important;
        }
      }
    }
  }

  .position {
    &-md {
      &-static {
        position: static !important;
      }
      &-relative {
        position: relative !important;
      }
      &-absolute {
        position: absolute !important;
      }
      &-fixed {
        position: fixed !important;
      }
      &-sticky {
        position: -webkit-sticky !important;
        position: sticky !important;
      }
    }
  }
}
@media (min-width: 992px) {
  .p-lg-6 {
    padding: 4.5rem !important;
  }
  .pt-lg-6,
  .py-lg-6 {
    padding-top: 4.5rem !important;
  }
  .pr-lg-6,
  .px-lg-6 {
    padding-right: 4.5rem !important;
  }
  .pb-lg-6,
  .py-lg-6 {
    padding-bottom: 4.5rem !important;
  }
  .pl-lg-6,
  .px-lg-6 {
    padding-left: 4.5rem !important;
  }
  .border {
    &-lg {
      &-0 {
        border: 0 solid !important;
      }
      &-1 {
        border: 1px solid !important;
      }
      &-2 {
        border: 2px solid !important;
      }
    }
    &-top {
      &-lg {
        &-0 {
          border-top: 0 solid !important;
        }
        &-1 {
          border-top: 1px solid !important;
        }
        &-2 {
          border-top: 2px solid !important;
        }
      }
    }
    &-right {
      &-lg {
        &-0 {
          border-right: 0 solid !important;
        }
        &-1 {
          border-right: 1px solid !important;
        }
        &-2 {
          border-right: 2px solid !important;
        }
      }
    }
    &-left {
      &-lg {
        &-0 {
          border-left: 0 solid !important;
        }
        &-1 {
          border-left: 1px solid !important;
        }
        &-2 {
          border-left: 2px solid !important;
        }
      }
    }
    &-bottom {
      &-lg {
        &-0 {
          border-bottom: 0 solid !important;
        }
        &-1 {
          border-bottom: 1px solid !important;
        }
        &-2 {
          border-bottom: 2px solid !important;
        }
      }
    }
  }

  .position {
    &-lg {
      &-static {
        position: static !important;
      }
      &-relative {
        position: relative !important;
      }
      &-absolute {
        position: absolute !important;
      }
      &-fixed {
        position: fixed !important;
      }
      &-sticky {
        position: -webkit-sticky !important;
        position: sticky !important;
      }
    }
  }
}
@media (min-width: 1200px) {
  .p-xl-6 {
    padding: 4.5rem !important;
  }
  .pt-xl-6,
  .py-xl-6 {
    padding-top: 4.5rem !important;
  }
  .pr-xl-6,
  .px-xl-6 {
    padding-right: 4.5rem !important;
  }
  .pb-xl-6,
  .py-xl-6 {
    padding-bottom: 4.5rem !important;
  }
  .pl-xl-6,
  .px-xl-6 {
    padding-left: 4.5rem !important;
  }
  .border {
    &-xl {
      &-0 {
        border: 0 solid !important;
      }
      &-1 {
        border: 1px solid !important;
      }
      &-2 {
        border: 2px solid !important;
      }
    }
    &-top {
      &-xl {
        &-0 {
          border-top: 0 solid !important;
        }
        &-1 {
          border-top: 1px solid !important;
        }
        &-2 {
          border-top: 2px solid !important;
        }
      }
    }
    &-right {
      &-xl {
        &-0 {
          border-right: 0 solid !important;
        }
        &-1 {
          border-right: 1px solid !important;
        }
        &-2 {
          border-right: 2px solid !important;
        }
      }
    }
    &-left {
      &-xl {
        &-0 {
          border-left: 0 solid !important;
        }
        &-1 {
          border-left: 1px solid !important;
        }
        &-2 {
          border-left: 2px solid !important;
        }
      }
    }
    &-bottom {
      &-xl {
        &-0 {
          border-bottom: 0 solid !important;
        }
        &-1 {
          border-bottom: 1px solid !important;
        }
        &-2 {
          border-bottom: 2px solid !important;
        }
      }
    }
  }

  .position {
    &-xl {
      &-static {
        position: static !important;
      }
      &-relative {
        position: relative !important;
      }
      &-absolute {
        position: absolute !important;
      }
      &-fixed {
        position: fixed !important;
      }
      &-sticky {
        position: -webkit-sticky !important;
        position: sticky !important;
      }
    }
  }
}
.no-border-last-child:last-child,
.no-border-first-child:first-child {
  border: 0 !important;
}

.phairify-heading-h3 {
  @include phairify_heading_bold_16;
}

.phairify-heading-not-bold-h4 {
  @include phairify_heading_not_bold_14;
}

.phairify-fade {
  background-color: rgba(255, 255, 255, 0.5);
}

.phairify-link {
  color: $phairify_primary_color_link;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    color: $phairify_primary_color_teal;
    cursor: pointer;
  }
}
.opacity-0 {
  opacity: 0 !important;
}
.opacity-10 {
  opacity: 0.1 !important;
}
.opacity-25 {
  opacity: 0.25 !important;
}
.opacity-50 {
  opacity: 0.5 !important;
}
.opacity-75 {
  opacity: 0.75 !important;
}
.opacity-100 {
  opacity: 1 !important;
}

.survey-results__responses-count {
  @include phairify_body_copy;
  color: $phairify_secondary_color_grey;
}

.phairify-dropdown {
  border-radius: 0;
  border-color: rgba($phairify_secondary_color_grey, 0.5);

  .dropdown-item:hover,
  .dropdown-item:focus {
    text-decoration: none;
    background-color: rgba($phairify_primary_color_teal, 0.25);
  }
}

@media (min-width: 1450px) {
  .d-xxl-none {
    display: none !important;
  }
  .d-xxl-inline {
    display: inline !important;
  }
  .d-xxl-inline-block {
    display: inline-block !important;
  }
  .d-xxl-block {
    display: block !important;
  }
  .d-xxl-table {
    display: table !important;
  }
  .d-xxl-table-row {
    display: table-row !important;
  }
  .d-xxl-table-cell {
    display: table-cell !important;
  }
  .d-xxl-flex {
    display: -webkit-box !important;
    display: -ms-flexbox !important;
    display: flex !important;
  }
  .d-xxl-inline-flex {
    display: -webkit-inline-box !important;
    display: -ms-inline-flexbox !important;
    display: inline-flex !important;
  }
}

.min-w-0 {
  min-width: 0 !important;
}
.min-w-a {
  min-width: auto !important;
}
.list-style-circle {
  list-style: circle !important;
}
.list-style-disc {
  list-style: disc !important;
}
.list-style-square {
  list-style: square !important;
}

.v2_phairify-tabs {
  border-bottom: 2px solid rgba($phairify_secondary_color_grey, 0.25) !important;
  .nav-link.phairify-tab {
    color: $phairify_secondary_color_grey;
    @extend .v2_font_Source;
    @extend .v2_font_size_16;
    @extend .v2_color_black;
    margin-bottom: -1px;
    background-color: transparent;
    border: 0;
    border-bottom: 2px solid transparent;
    border-radius: 0 !important;

    &:hover {
      border-color: $phairify_secondary_color_sky;
      color: $phairify_secondary_color_sky !important;
    }
    &.phairify-tab-active {
      background-color: transparent !important;
      border-color: $phairify_secondary_color_lime !important;
      color: $phairify_secondary_color_lime !important;
    }
  }
}

.phairify-background-teal-1 {
  background-color: rgba($phairify_primary_color_teal, 0.1);
}
.phairify-background-teal-5 {
  background-color: rgba($phairify_primary_color_teal, 0.5);
}
.phairify-background-teal-10 {
  background-color: $phairify_primary_color_teal;
}
.phairify-background-teal {
  background-color: $phairify_primary_color_teal;
}
.phairify-background-navy-1 {
  background-color: rgba($phairify_primary_color_navy, 0.1);
}
.phairify-background-navy-5 {
  background-color: rgba($phairify_primary_color_navy, 0.5);
}
.phairify-background-navy-10 {
  background-color: $phairify_primary_color_navy;
}
.phairify-background-navy {
  background-color: $phairify_primary_color_navy;
}
.phairify-background-lime-1 {
  background-color: rgba($phairify_secondary_color_lime, 0.1);
}
.phairify-background-lime-5 {
  background-color: rgba($phairify_secondary_color_lime, 0.5);
}
.phairify-background-lime-10 {
  background-color: $phairify_secondary_color_lime;
}
.phairify-background-lime {
  background-color: $phairify_secondary_color_lime;
}
.border-navy {
  border-color: $phairify_primary_color_navy !important;
}
.border-teal {
  border-color: $phairify_primary_color_teal !important;
}
.border-lime {
  border-color: $phairify_secondary_color_lime !important;
}
.border-sky {
  border-color: $phairify_secondary_color_sky !important;
}
.border-grey {
  border-color: $phairify_secondary_color_grey !important;
}
.border-grey-50 {
  border-color: rgba($phairify_secondary_color_grey, 0.5) !important;
}
.border-green {
  border-color: $phairify_color_green !important;
}
.h-90 {
  height: 90%;
}
.overflow {
  &-scroll {
    &-y {
      overflow-y: scroll;
    }
    &-x {
      overflow-x: scroll;
    }
  }
  &-scroll {
    &-y-auto {
      overflow-y: auto;
    }
    &-x-auto {
      overflow-x: auto;
    }
  }
}
.bottom-0 {
  bottom: 0;
}
.left-0 {
  left: 0;
}
.right-0 {
  right: 0;
}
.top-0 {
  top: 0;
}
.text {
  &_underline {
    text-decoration: underline;
  }
}
.no-wrap {
  white-space: nowrap;
}
.overflow-hidden {
  overflow: hidden;
}
.vertical_align_middle {
  vertical-align: middle !important;
}
.phairify-no-outline-on-focus {
  &:focus {
    outline: none !important;
  }
}

.v2_phairify-select-wrapper {
  min-width: 215px;
  .custom-select {
    height: 100%;
    padding: 15px;
    border-radius: 4px;
  }
}
@import '../sass/v2.scss';

.board-certification-item {
  cursor: pointer;
  &.eligible {
    @extend .v3_color_lime;
  }
  &.certified {
    @extend .v3_color_teal;
  }
  &.selected {
    background-color: rgba($phairify_v2_color_secondary_sky, 0.75);
  }
}

.special-header {
  color: teal;
  text-transform: uppercase;
  letter-spacing: 0.2rem;
}
ul.green-dot-list {
  list-style: none;
  padding-left: 2rem;

  & li {
    display: flex;
    align-items: center;

    &:before {
      content: "\2022";
      display: inline-block;
      width: 1em;
      margin-left: -1em;
      color: yellowgreen;
      font-size: 2rem;
      font-weight: bold;
      line-height: 2rem;
    }
  }
}

/*   Criteria button enable/disable CSS */
.btn-disable-btn{
  display: inline-flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  max-height: 30px;
  font: normal normal 550 16px/24px;
  letter-spacing: 0;
  color: #00586f !important;
  background-color: #99c555 !important;
  border: 1px solid #00586f !important;
  border-radius:0;
  border-color: #00586f !important;
  opacity: 1;
}
.btn-disable-btn:hover{
  background-color: #b0d27b !important;
}
.btn-disable-btn:disabled{
  display: inline-flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  max-height: 30px;
  font: normal normal 550 16px/24px;
  letter-spacing: 0;
  color: #00586f !important;
  background-color: #dee9ec !important;
  border: 1px solid #00586f !important;
  border-color: #00586f !important;
  opacity: 1;
}
.btn-disable-btn i{
  font-size: 15px;
}

@media (max-width:767px){
  .btn-disable-btn{
    max-height:unset !important;
  }
  .mobile-margin-btn{
    margin-bottom: 1rem;
  }
  .flex-flow-column{
    flex-flow: column;
  }
}

/* Other common usage CSS */
.w-100{
  width:100%;
}
.criteria-inner-form-main-div{
  height: 100% !important;display: flex;flex-flow: column;justify-content: space-between;
}

</style>
